/**
 * LiveUploadMonitor Component - Sprint 10
 * 
 * Real-time upload progress visualization
 * Features:
 * - Live progress bar with chunk-level granularity
 * - Real-time upload speed display
 * - ETA (Estimated Time of Arrival) calculation
 * - Pause/Resume/Cancel controls
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Progress, Button, Space, Statistic, Row, Col, Tag } from 'antd';
import {
  CloudUploadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import logger from '../../utils/logger';
import './LiveUploadMonitor.scss';

export interface LiveUploadProgress {
  videoGuid: string;
  progress: number;
  speed: number;
  eta: number;
  uploadedChunks: number;
  totalChunks: number;
  status: 'uploading' | 'paused' | 'completed' | 'error';
}

interface LiveUploadMonitorProps {
  uploads: LiveUploadProgress[];
  onPause?: (videoGuid: string) => void;
  onResume?: (videoGuid: string) => void;
  onCancel?: (videoGuid: string) => void;
}

export const LiveUploadMonitor: React.FC<LiveUploadMonitorProps> = ({
  uploads,
  onPause,
  onResume,
  onCancel,
}) => {
  const [expandedUpload, setExpandedUpload] = useState<string | null>(null);

  const formatSpeed = (bytesPerSecond: number): string => {
    const mbps = (bytesPerSecond * 8) / (1024 * 1024);
    if (mbps >= 1) {
      return `${mbps.toFixed(2)} Mbps`;
    }
    const kbps = (bytesPerSecond * 8) / 1024;
    return `${kbps.toFixed(2)} Kbps`;
  };

  const formatETA = (seconds: number): string => {
    if (!isFinite(seconds) || seconds <= 0) {
      return 'Calculating...';
    }
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    
    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getStatusColor = (status: LiveUploadProgress['status']): string => {
    switch (status) {
      case 'uploading':
        return 'processing';
      case 'paused':
        return 'warning';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: LiveUploadProgress['status']): string => {
    switch (status) {
      case 'uploading':
        return 'Uploading';
      case 'paused':
        return 'Paused';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const handleToggleExpand = (videoGuid: string) => {
    setExpandedUpload(expandedUpload === videoGuid ? null : videoGuid);
  };

  if (uploads.length === 0) {
    return null;
  }

  return (
    <div className="live-upload-monitor">
      {uploads.map((upload) => {
        const isExpanded = expandedUpload === upload.videoGuid;
        
        return (
          <Card
            key={upload.videoGuid}
            className={`upload-card ${upload.status}`}
            size="small"
            title={
              <Space>
                <CloudUploadOutlined />
                <span>Upload {upload.videoGuid.substring(0, 8)}...</span>
                <Tag color={getStatusColor(upload.status)}>
                  {getStatusText(upload.status)}
                </Tag>
              </Space>
            }
            extra={
              <Button
                type="text"
                size="small"
                onClick={() => handleToggleExpand(upload.videoGuid)}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </Button>
            }
          >
            {/* Compact Progress Bar */}
            <div className="progress-section">
              <Progress
                percent={Math.round(upload.progress)}
                status={upload.status === 'error' ? 'exception' : undefined}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              
              <Row gutter={16} className="stats-row">
                <Col span={8}>
                  <div className="stat-item">
                    <ThunderboltOutlined className="stat-icon" />
                    <span className="stat-value">{formatSpeed(upload.speed)}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="stat-item">
                    <ClockCircleOutlined className="stat-icon" />
                    <span className="stat-value">{formatETA(upload.eta)}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="stat-item">
                    <span className="stat-value">
                      {upload.uploadedChunks}/{upload.totalChunks} chunks
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <>
                <Row gutter={16} className="detailed-stats">
                  <Col span={12}>
                    <Statistic
                      title="Upload Speed"
                      value={formatSpeed(upload.speed)}
                      prefix={<ThunderboltOutlined />}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="ETA"
                      value={formatETA(upload.eta)}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Col>
                </Row>

                <Row gutter={16} className="detailed-stats">
                  <Col span={12}>
                    <Statistic
                      title="Progress"
                      value={upload.progress}
                      suffix="%"
                      precision={1}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Chunks"
                      value={`${upload.uploadedChunks}/${upload.totalChunks}`}
                    />
                  </Col>
                </Row>

                {/* Upload Controls */}
                <div className="upload-controls">
                  <Space>
                    {upload.status === 'uploading' && onPause && (
                      <Button
                        type="default"
                        icon={<PauseCircleOutlined />}
                        onClick={() => onPause(upload.videoGuid)}
                      >
                        Pause
                      </Button>
                    )}
                    
                    {upload.status === 'paused' && onResume && (
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => onResume(upload.videoGuid)}
                      >
                        Resume
                      </Button>
                    )}
                    
                    {(upload.status === 'uploading' || upload.status === 'paused') && onCancel && (
                      <Button
                        type="default"
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => onCancel(upload.videoGuid)}
                      >
                        Cancel
                      </Button>
                    )}
                  </Space>
                </div>
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default LiveUploadMonitor;
